import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Send } from "lucide-react";
import { MicroLabel } from "@/components/decor/CropMarks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getAttribution, getSubmittedPage } from "@/lib/utm";

const SERVICES = [
  "Print Media",
  "Corporate Gifting",
  "Signage & Display",
  "Promotional Products",
  "Brand Development Support",
  "Digital & Media Extension",
];
const REQUIREMENT_TYPES = ["One-time Project", "Recurring", "Ongoing Retainer", "Exploratory"];
const BUDGETS = ["Under ₹50K", "₹50K – ₹2L", "₹2L – ₹5L", "₹5L – ₹10L", "₹10L+"];
const TIMELINES = ["Within 2 weeks", "2–4 weeks", "1–2 months", "Flexible"];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  requirement: "",
  budget: "",
  timeline: "",
  message: "",
};

const inputStyle = {
  background: "transparent",
  border: "none",
  borderBottom: "1px solid rgba(192,192,192,0.25)",
  color: "#F5F1E8",
  fontFamily: "Montserrat",
  fontSize: 15,
  padding: "12px 0",
  height: "auto",
  borderRadius: 0,
};

const labelStyle = {
  color: "#B89B5E",
  fontFamily: "Montserrat",
  fontSize: 10,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  fontWeight: 600,
  marginBottom: 8,
  display: "block",
};

const Field = ({ children }) => <div className="flex flex-col">{children}</div>;

const ContactQuote = () => {
  const [data, setData] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (v) =>
    setData((d) => ({ ...d, [k]: typeof v === "string" ? v : v.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...data,
        submittedPage: getSubmittedPage(),
        attribution: getAttribution(),
      });
      toast.success("Quote request received. Our team will reach you shortly.", {
        description: "Thank you for reaching out to Krister Overseas.",
      });
      setData(EMPTY);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error("Could not send right now.", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative"
      style={{ background: "#0A0A0A", paddingTop: 120, paddingBottom: 120 }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left: pitch */}
        <div className="lg:col-span-5">
          <MicroLabel index={12} accent="#B89B5E">
            Contact / Quote
          </MicroLabel>
          <h2
            className="mt-6"
            style={{
              fontFamily: "Montserrat",
              fontWeight: 800,
              color: "#F5F1E8",
              fontSize: "clamp(34px, 4.6vw, 60px)",
              lineHeight: 1,
              letterSpacing: "-0.025em",
              textTransform: "uppercase",
            }}
          >
            Start a project.
            <br />
            <span style={{ color: "#B89B5E" }}>Build presence.</span>
          </h2>
          <p
            className="mt-6"
            style={{
              color: "#C0C0C0",
              fontSize: 15,
              lineHeight: 1.75,
              maxWidth: 420,
            }}
          >
            Tell us about your brief. We will respond with materials, finishes,
            timelines and a clear quote — no jargon, no padding.
          </p>

          <div className="mt-10 space-y-4">
            <div>
              <div className="ko-overline" style={{ color: "#C0C0C0" }}>
                Email
              </div>
              <a
                href="mailto:contact@kristeroverseas.com"
                className="mt-1 block hover:opacity-80 transition-opacity"
                style={{ color: "#F5F1E8", fontSize: 16, fontWeight: 500 }}
                data-testid="contact-email"
              >
                contact@kristeroverseas.com
              </a>
            </div>
            <div>
              <div className="ko-overline" style={{ color: "#C0C0C0" }}>
                Phone
              </div>
              <div
                className="mt-1 flex flex-wrap gap-x-4 gap-y-1"
                style={{ color: "#F5F1E8", fontSize: 16, fontWeight: 500 }}
                data-testid="contact-phone"
              >
                <a href="tel:+919810080821" className="hover:opacity-80 transition-opacity">
                  +91 98100 80821
                </a>
                <span style={{ color: "#C0C0C0" }}>·</span>
                <a href="tel:+919310080821" className="hover:opacity-80 transition-opacity">
                  +91 93100 80821
                </a>
              </div>
            </div>
            <div>
              <div className="ko-overline" style={{ color: "#C0C0C0" }}>
                Location
              </div>
              <div
                className="mt-1"
                style={{ color: "#F5F1E8", fontSize: 16, fontWeight: 500 }}
                data-testid="contact-location"
              >
                Delhi, India
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={handleSubmit}
          data-testid="quote-form"
          className="lg:col-span-7 relative p-7 md:p-10"
          style={{
            background: "#0F0F0F",
            border: "1px solid rgba(192,192,192,0.15)",
          }}
          noValidate
        >
          <div
            aria-hidden
            className="absolute top-0 left-0"
            style={{ width: 48, height: 3, background: "#B89B5E" }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Field>
              <Label style={labelStyle} htmlFor="qf-name">Name *</Label>
              <Input
                id="qf-name"
                data-testid="qf-name"
                value={data.name}
                onChange={setField("name")}
                placeholder="Your full name"
                style={inputStyle}
                autoComplete="name"
                required
              />
            </Field>
            <Field>
              <Label style={labelStyle} htmlFor="qf-email">Email *</Label>
              <Input
                id="qf-email"
                data-testid="qf-email"
                type="email"
                value={data.email}
                onChange={setField("email")}
                placeholder="you@brand.com"
                style={inputStyle}
                autoComplete="email"
                required
              />
            </Field>
            <Field>
              <Label style={labelStyle} htmlFor="qf-phone">Phone</Label>
              <Input
                id="qf-phone"
                data-testid="qf-phone"
                value={data.phone}
                onChange={setField("phone")}
                placeholder="+91 98100 80821"
                style={inputStyle}
                autoComplete="tel"
              />
            </Field>
            <Field>
              <Label style={labelStyle} htmlFor="qf-company">Company</Label>
              <Input
                id="qf-company"
                data-testid="qf-company"
                value={data.company}
                onChange={setField("company")}
                placeholder="Company / Brand"
                style={inputStyle}
                autoComplete="organization"
              />
            </Field>

            <Field>
              <Label style={labelStyle}>Service Interested In</Label>
              <Select value={data.service} onValueChange={setField("service")}>
                <SelectTrigger
                  data-testid="qf-service"
                  style={{ ...inputStyle, height: 44 }}
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent
                  style={{
                    background: "#0A0A0A",
                    border: "1px solid rgba(192,192,192,0.2)",
                    color: "#F5F1E8",
                    borderRadius: 0,
                  }}
                >
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s} style={{ color: "#F5F1E8" }}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <Label style={labelStyle}>Requirement Type</Label>
              <Select value={data.requirement} onValueChange={setField("requirement")}>
                <SelectTrigger
                  data-testid="qf-requirement"
                  style={{ ...inputStyle, height: 44 }}
                >
                  <SelectValue placeholder="One-time / Recurring" />
                </SelectTrigger>
                <SelectContent
                  style={{
                    background: "#0A0A0A",
                    border: "1px solid rgba(192,192,192,0.2)",
                    color: "#F5F1E8",
                    borderRadius: 0,
                  }}
                >
                  {REQUIREMENT_TYPES.map((s) => (
                    <SelectItem key={s} value={s} style={{ color: "#F5F1E8" }}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <Label style={labelStyle}>Budget</Label>
              <Select value={data.budget} onValueChange={setField("budget")}>
                <SelectTrigger
                  data-testid="qf-budget"
                  style={{ ...inputStyle, height: 44 }}
                >
                  <SelectValue placeholder="Indicative range" />
                </SelectTrigger>
                <SelectContent
                  style={{
                    background: "#0A0A0A",
                    border: "1px solid rgba(192,192,192,0.2)",
                    color: "#F5F1E8",
                    borderRadius: 0,
                  }}
                >
                  {BUDGETS.map((s) => (
                    <SelectItem key={s} value={s} style={{ color: "#F5F1E8" }}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <Label style={labelStyle}>Timeline</Label>
              <Select value={data.timeline} onValueChange={setField("timeline")}>
                <SelectTrigger
                  data-testid="qf-timeline"
                  style={{ ...inputStyle, height: 44 }}
                >
                  <SelectValue placeholder="When do you need it?" />
                </SelectTrigger>
                <SelectContent
                  style={{
                    background: "#0A0A0A",
                    border: "1px solid rgba(192,192,192,0.2)",
                    color: "#F5F1E8",
                    borderRadius: 0,
                  }}
                >
                  {TIMELINES.map((s) => (
                    <SelectItem key={s} value={s} style={{ color: "#F5F1E8" }}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <div className="md:col-span-2">
                <Label style={labelStyle} htmlFor="qf-message">Message *</Label>
                <Textarea
                  id="qf-message"
                  data-testid="qf-message"
                  value={data.message}
                  onChange={setField("message")}
                  placeholder="Tell us about the project, quantities, materials, timeline..."
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical" }}
                  required
                />
              </div>
            </Field>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="ko-overline" style={{ color: "#C0C0C0" }}>
              Phase 1 · Lead capture live
            </div>
            <button
              type="submit"
              data-testid="qf-submit"
              disabled={submitting}
              className="ko-overline inline-flex items-center gap-3 px-7 py-4 transition-all duration-300 disabled:opacity-60"
              style={{
                background: "#F5F1E8",
                color: "#0A0A0A",
                fontSize: 11,
                letterSpacing: "0.24em",
                fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.background = "#B89B5E";
              }}
              onMouseLeave={(e) => {
                if (!submitting) e.currentTarget.style.background = "#F5F1E8";
              }}
            >
              {submitting ? "Sending..." : "Send Request"}
              {submitting ? <Send size={14} strokeWidth={1.5} /> : <ArrowRight size={14} strokeWidth={1.5} />}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactQuote;
